import { ArrowUp, Plus, UserRound, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { CharacterPicker } from '@/components/CharacterPicker';
import {
  ASPECT_KEYS,
  ASPECTS,
  type Aspect,
  frameSize,
  LANGUAGES,
  type ProjectDto,
  QUALITIES,
  SIZE_KEYS,
  SIZES,
  STYLE_PRESETS,
  VIDEO_MODELS,
  VIDEO_MODEL_NAMES,
  VIDEO_MODEL_NOTES,
  type VideoModel,
  type VideoSize,
} from '@studio/shared';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useProjectCall } from '@/components/project/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { NativeSelect } from '@/components/ui/controls';
import { Field, Input } from '@/components/ui/form';
import { useCharacters, useProducts } from '@/lib/queries';

/** The project's settings: title, product, language, style, video model, shape, size, quality and its characters (S1, S2, …). */
export function ProjectSettingsCard({ project }: { project: ProjectDto }) {
  const call = useProjectCall(project.id);
  const { data: products } = useProducts();
  const { data: characters } = useCharacters();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [title, setTitle] = useState(project.title);
  const [productId, setProductId] = useState(project.productId ?? '');
  const [language, setLanguage] = useState(project.language);
  const [style, setStyle] = useState(project.style);
  const [aspect, setAspect] = useState<Aspect>(project.aspect);
  const [quality, setQuality] = useState(project.quality);
  const [videoModel, setVideoModel] = useState<VideoModel>((project.videoModel as VideoModel) ?? 'freevideo');
  const [size, setSize] = useState<VideoSize>(project.size ?? 'standard');
  const [castIds, setCastIds] = useState(project.castIds);
  const [saving, setSaving] = useState(false);
  // Another tab (or the director) may change the project: show what it is now.
  useEffect(() => {
    setTitle(project.title);
    setProductId(project.productId ?? '');
    setLanguage(project.language);
    setStyle(project.style);
    setAspect(project.aspect);
    setQuality(project.quality);
    setVideoModel((project.videoModel as VideoModel) ?? 'freevideo');
    setSize(project.size ?? 'standard');
    setCastIds(project.castIds);
  }, [project.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setSaving(true);
    const r = await call(`/api/projects/${project.id}`, {
      method: 'PATCH',
      body: { title, productId: productId || null, language, style, aspect, quality, videoModel, size, castIds },
    });
    setSaving(false);
    if (r) toast.success('Settings saved');
  };
  const customStyle = !STYLE_PRESETS.includes(style as (typeof STYLE_PRESETS)[number]);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Settings</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4">
        <Field label="Title">{(p) => <Input {...p} value={title} onChange={(e) => setTitle(e.target.value)} />}</Field>
        <Field label="Product">
          {(p) => (
            <NativeSelect {...p} value={productId} onChange={(e) => setProductId(e.target.value)}>
              <option value="">— none —</option>
              {products?.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.title}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label="Language of the dialogue">
          {(p) => (
            <NativeSelect {...p} value={language} onChange={(e) => setLanguage(e.target.value)}>
              {LANGUAGES.map((l) => (
                <option key={l}>{l}</option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label="Style">
          {(p) => (
            <NativeSelect
              {...p}
              value={customStyle ? '__custom' : style}
              onChange={(e) => setStyle(e.target.value === '__custom' ? '' : e.target.value)}
            >
              {STYLE_PRESETS.map((s) => (
                <option key={s}>{s}</option>
              ))}
              <option value="__custom">Custom…</option>
            </NativeSelect>
          )}
        </Field>
        {customStyle && (
          <Input value={style} onChange={(e) => setStyle(e.target.value)} placeholder="Describe the style in English" />
        )}
        <Field label="Video model" hint={VIDEO_MODEL_NOTES[videoModel]}>
          {(p) => (
            <NativeSelect {...p} value={videoModel} onChange={(e) => setVideoModel(e.target.value as VideoModel)}>
              {VIDEO_MODELS.map((m) => (
                <option key={m} value={m}>
                  {VIDEO_MODEL_NAMES[m]}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label="Shape" hint="Shots already made keep their shape.">
          {(p) => (
            <NativeSelect {...p} value={aspect} onChange={(e) => setAspect(e.target.value as Aspect)}>
              {ASPECT_KEYS.map((a) => (
                <option key={a} value={a}>
                  {ASPECTS[a].label}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Size">
            {(p) => (
              <NativeSelect {...p} value={size} onChange={(e) => setSize(e.target.value as VideoSize)}>
                {SIZE_KEYS.map((k) => (
                  <option key={k} value={k}>
                    {SIZES[k].label} ({frameSize(aspect, k).outWidth}×{frameSize(aspect, k).outHeight})
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label="Quality">
            {(p) => (
              <NativeSelect {...p} value={quality} onChange={(e) => setQuality(e.target.value)}>
                {QUALITIES.map((q) => (
                  <option key={q}>{q}</option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <p className="col-span-2 -mt-1 text-xs text-muted-foreground">
            {SIZES[size].hint} Quality: light is the fastest (the demo ad used it); higher qualities take longer per
            shot. Shots already made keep their size until remade.
          </p>
        </div>
        <div>
          <div className="mb-1.5 text-sm font-medium">Characters (in order: S1, S2, …)</div>
          <div className="space-y-2">
            {castIds.map((id, i) => {
              const c = (characters ?? project.cast).find((item) => item.id === id);
              if (!c) return null;
              return <div key={id} className="flex items-center gap-3 rounded-md border p-2 text-sm">
                {/* The order gives the speaker IDs (S1, S2, …) the prompts use. */}
                <Badge variant="secondary" className="shrink-0">S{i + 1}</Badge>
                {c.images[0]
                  ? <img src={c.images[0].url} alt="" className="size-12 shrink-0 rounded-md object-cover" />
                  : <UserRound aria-hidden className="size-12 shrink-0 rounded-md bg-muted p-3 text-muted-foreground" />}
                <span className="min-w-0 flex-1 break-words">{c.name}</span>
                <Button type="button" variant="ghost" size="icon" className="shrink-0" aria-label={"Move " + c.name + " up"} title="Move up (speaker order)" disabled={i === 0} onClick={() => setCastIds((ids) => ids.map((x, j) => (j === i - 1 ? id : j === i ? ids[i - 1]! : x)))}><ArrowUp /></Button>
                <Button type="button" variant="ghost" size="icon" className="shrink-0" aria-label={"Unselect " + c.name} title="Unselect character" onClick={() => setCastIds((ids) => ids.filter((selectedId) => selectedId !== id))}><X /></Button>
              </div>;
            })}
            {!castIds.length && <p className="text-sm text-muted-foreground">No characters selected.</p>}
            <Button type="button" variant="outline" onClick={() => setPickerOpen(true)}><Plus /> Add a character</Button>
          </div>
          <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
            <DialogContent className="flex max-w-2xl flex-col overflow-hidden">
              <DialogHeader>
                <DialogTitle>Add a character</DialogTitle>
                <DialogDescription>Choose characters from your folders. You can search, add, rename or delete characters here.</DialogDescription>
              </DialogHeader>
              <div className="min-h-0 overflow-y-auto overscroll-contain">
                <CharacterPicker value={castIds} onChange={setCastIds} />
              </div>
              <DialogFooter className="shrink-0">
                <Button type="button" onClick={() => setPickerOpen(false)}>Done</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <Button onClick={() => void save()} loading={saving}>
          Save settings
        </Button>
      </CardContent>
    </Card>
  );
}
